import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Tag, TrendingUp, BookOpen } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axios from 'axios';

const API = '/api';

// Category images from Unsplash - Car related photos
const categoryImages = {
  financing: [
    'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=800&q=80',
    'https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=800&q=80',
    'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=800&q=80',
    'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=800&q=80',
  ],
  credit: [
    'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&q=80',
    'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800&q=80',
    'https://images.unsplash.com/photo-1542362567-b07e54358753?w=800&q=80',
    'https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=800&q=80',
  ],
  industry: [
    'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=800&q=80',
    'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800&q=80',
    'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?w=800&q=80',
    'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=800&q=80',
  ],
  guides: [
    'https://images.unsplash.com/photo-1580273916550-e323be2ae537?w=800&q=80',
    'https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=800&q=80',
    'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800&q=80',
    'https://images.unsplash.com/photo-1619767886558-efdc259cde1a?w=800&q=80',
  ]
};

const Blog = () => {
  const { language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', labelEn: 'All Articles', labelFr: 'Tous les articles', icon: BookOpen, color: 'bg-gray-700' },
    { id: 'financing', labelEn: 'Auto Financing', labelFr: 'Financement auto', icon: TrendingUp, color: 'bg-blue-500' },
    { id: 'credit', labelEn: 'Credit Tips', labelFr: 'Conseils crédit', icon: Tag, color: 'bg-purple-500' },
    { id: 'industry', labelEn: 'Industry News', labelFr: 'Actualités', icon: TrendingUp, color: 'bg-orange-500' },
    { id: 'guides', labelEn: 'Buying Guides', labelFr: "Guides d'achat", icon: BookOpen, color: 'bg-green-500' }
  ];

  useEffect(() => {
    fetchPosts();
    document.title = language === 'en'
      ? 'Auto Financing Blog | Credit Auto Reset - Tips & Guides Quebec'
      : 'Blogue Financement Auto | Credit Auto Reset - Conseils et Guides Québec';
    document.querySelector('meta[name="description"]')?.setAttribute('content',
      language === 'en'
        ? 'Expert articles on auto financing, credit recovery, and car buying tips in Quebec. Learn how to get approved with bad credit.'
        : 'Articles experts sur le financement auto, le rétablissement de crédit et conseils d\'achat de voiture au Québec. Apprenez comment être approuvé avec mauvais crédit.'
    );
  }, [language]);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    }
    setLoading(false);
  };

  const filteredPosts = selectedCategory === 'all' 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'en' ? 'en-CA' : 'fr-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getImageForPost = (post, index) => {
    if (post.image_url) return post.image_url;
    const images = categoryImages[post.category] || categoryImages.financing;
    return images[index % images.length];
  };

  const getCategoryInfo = (categoryId) => {
    return categories.find(c => c.id === categoryId) || categories[0];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a5c3b] via-[#165432] to-[#0d3321] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="bg-white/20 text-white mb-6 px-4 py-2">
              <BookOpen className="w-4 h-4 mr-2" />
              {language === 'en' ? 'Auto Financing Blog' : 'Blogue Financement Auto'}
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              {language === 'en' ? 'Expert Tips & Industry News' : 'Conseils d\'experts et actualités'}
            </h1>
            <p className="text-xl text-green-100 max-w-2xl mx-auto">
              {language === 'en' 
                ? 'Stay informed about auto financing, credit tips, and industry trends in Quebec.'
                : 'Restez informé sur le financement auto, les conseils de crédit et les tendances au Québec.'}
            </p>
          </div>
        </div>
      </section>

      {/* Categories */}
      <div className="container mx-auto px-4 -mt-8 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl p-4 max-w-4xl mx-auto">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold transition-all ${
                    selectedCategory === cat.id
                      ? `${cat.color} text-white shadow-lg scale-105`
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {language === 'en' ? cat.labelEn : cat.labelFr}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Blog Posts */}
      <div className="container mx-auto px-4 py-16">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-12 h-12 border-4 border-[#1a5c3b] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <BookOpen className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              {language === 'en' ? 'No articles yet' : 'Aucun article pour le moment'}
            </h3>
            <p className="text-gray-500">
              {language === 'en' ? 'Check back soon for new content!' : 'Revenez bientôt pour du nouveau contenu!'}
            </p>
          </div>
        ) : (
          <>
            {/* Featured Post (first post) */}
            {filteredPosts.length > 0 && (
              <div className="mb-12">
                <Link to={`/blog/${filteredPosts[0].slug}`}>
                  <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300 bg-white group">
                    <div className="grid md:grid-cols-2">
                      <div className="h-64 md:h-auto overflow-hidden">
                        <img 
                          src={getImageForPost(filteredPosts[0], 0)} 
                          alt={language === 'en' ? filteredPosts[0].title_en : filteredPosts[0].title_fr}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <CardContent className="p-8 flex flex-col justify-center">
                        <Badge className={`${getCategoryInfo(filteredPosts[0].category).color} text-white w-fit mb-4`}>
                          <Tag className="w-3 h-3 mr-1" />
                          {getCategoryInfo(filteredPosts[0].category)[language === 'en' ? 'labelEn' : 'labelFr']}
                        </Badge>
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 group-hover:text-[#1a5c3b] transition-colors">
                          {language === 'en' ? filteredPosts[0].title_en : filteredPosts[0].title_fr}
                        </h2>
                        <p className="text-gray-600 mb-6 line-clamp-3">
                          {language === 'en' ? filteredPosts[0].excerpt_en : filteredPosts[0].excerpt_fr}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="flex items-center text-sm text-gray-500">
                            <Calendar className="w-4 h-4 mr-2" />
                            {formatDate(filteredPosts[0].created_at)}
                          </span>
                          <Button variant="ghost" className="text-[#1a5c3b] group-hover:translate-x-2 transition-transform">
                            {language === 'en' ? 'Read Article' : 'Lire l\'article'}
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                </Link>
              </div>
            )}

            {/* Other Posts Grid */}
            {filteredPosts.length > 1 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.slice(1).map((post, index) => (
                  <Link key={post.id} to={`/blog/${post.slug}`}>
                    <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 bg-white h-full group">
                      <div className="h-52 overflow-hidden relative">
                        <img 
                          src={getImageForPost(post, index + 1)} 
                          alt={language === 'en' ? post.title_en : post.title_fr}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute top-4 left-4">
                          <Badge className={`${getCategoryInfo(post.category).color} text-white`}>
                            {getCategoryInfo(post.category)[language === 'en' ? 'labelEn' : 'labelFr']}
                          </Badge>
                        </div>
                      </div>
                      <CardContent className="p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-[#1a5c3b] transition-colors">
                          {language === 'en' ? post.title_en : post.title_fr}
                        </h2>
                        <p className="text-gray-600 mb-4 line-clamp-2 text-sm">
                          {language === 'en' ? post.excerpt_en : post.excerpt_fr}
                        </p>
                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                          <span className="flex items-center text-sm text-gray-500">
                            <Calendar className="w-4 h-4 mr-1" />
                            {formatDate(post.created_at)}
                          </span>
                          <span className="text-[#1a5c3b] font-medium text-sm flex items-center group-hover:translate-x-1 transition-transform">
                            {language === 'en' ? 'Read' : 'Lire'}
                            <ArrowRight className="w-4 h-4 ml-1" />
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-[#1a5c3b] to-[#165432] text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            {language === 'en' ? 'Ready to Get Approved?' : 'Prêt à être approuvé?'}
          </h2>
          <p className="text-green-100 mb-8 max-w-2xl mx-auto">
            {language === 'en' 
              ? 'Apply now and get a response within 24 hours! Bad credit? No problem.'
              : 'Faites votre demande maintenant et obtenez une réponse en 24h! Mauvais crédit? Pas de problème.'}
          </p>
          <Link to="/demander-un-pret-auto">
            <Button size="lg" className="bg-white text-[#1a5c3b] hover:bg-gray-100 px-8 py-6 text-lg">
              {language === 'en' ? 'Apply Now - It\'s Free!' : 'Faire une demande - C\'est gratuit!'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Blog;
